<script setup>
import MyBetFooterAmount from './MyBetFooterAmount.vue';
import { useBetsStore } from '@/stores/bets';
import { storeToRefs } from 'pinia';
import { computed, ref, onMounted } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useI18n } from 'vue-i18n';
import { timestampToDayMonth, timestampToTime, toFixedTwo, timestampToDayMonthString, safeTranslate } from '@/helpers';
import { formatMarketName } from '@/helpers'

const props = defineProps({
    ticket: Object,
})

const { t, te } = useI18n()
const auth = useAuthStore()
const bets = useBetsStore()
const { getCurrency, getUser } = storeToRefs(auth)
const collapsed = ref(true)
const betslips = ref([])
const date = computed(() => timestampToDayMonth(props.ticket.createdAt))

const marketName = (slip) => {
  const market = {
    i: slip.mfid,          
    n: slip.mnm,            
    nrs: slip.mnrs || []   
  }
  const event = {
    h: slip.ht,
    a: slip.at
  }
  return formatMarketName(market, event)
}
const selectionTranslation = (selection) => { 
    return safeTranslate('selections', selection.oddnm, t, te)
}

const fetchSlips = async () => {
    if (collapsed.value) {
        await bets.fetchBetslips(props.ticket._id).then(res => {
            if (res.data.ok) {
                betslips.value = res.data.data
                collapsed.value = false
            }
        })
    } else {
        collapsed.value = true
    }
}

onMounted(() => {
  if (!collapsed.value ) {
    bets.fetchBetslips(props.ticket._id).then(res => {
      if (res.data.ok) {
        betslips.value = res.data.data;
      }
    });
  }
});
</script>

<template>
    <div class="myb-Ticket">
        <div class="myb-TicketHeader" @click="fetchSlips">
            <div class="myb-TicketHeaderNr">
                <p>
                    <template v-if="getUser.rl == 'clt'">{{ ticket.nr }}</template>
                    <template v-else>{{ ticket.unm }}</template>
                </p>
                <p class="text-xs font-normal text-neutral-400" >
                    {{ date[0] }} {{ date[1] }} {{ date[2] }}
                    {{ timestampToTime(ticket.createdAt) }}
                </p>
            </div>
            <div class="myb-TicketHeaderAct">
                <div
                    v-if="ticket.st != 6"
                    :class="[
                        'myb-TicketStatus',
                        {'win': ticket.st == 2},
                        {'lost': ticket.st == 1}
                    ]"
                >
                    <span class="mr-1.5">{{ t('ticket.' + ticket.st) }}</span>
                </div>
            </div>
        </div>
        <div v-if="!collapsed" class="myb-TicketBetslips">
            <ul class="list-none space-y-5">
                <li v-for="slip in betslips">
                    <div class="myb-TicketBetslip">
                        <div class="flex-shrink-0 mt-1 mr-1.5">
                            <img src="/win.svg" class="w-3 h-3" v-if="slip.st == 2" />
                            <img src="/lost.svg" class="w-3 h-3" v-if="slip.st == 1" />
                            <div class="w-3 h-3 bg-neutral-400 rounded-full" v-if="slip.st != 2 && slip.st != 1" />
                        </div>
                        <div class="flex-1 cursor-pointer">
                            <div class="text-neutral-400 text-xs" v-if="slip.tm">
                                <p>{{timestampToDayMonthString(slip.tm)}} {{ timestampToTime(slip.tm)}}</p>
                            </div>
                            <p class="text-neutral-200 font-bold text-sm relative myb-SlipTeams">
                                {{ slip.ht }} v {{ slip.at }}
                            </p>
                            <div class="mt-1 flex justify-between">
                                <p class="text-neutral-400 text-xs">
                                    <span>{{ marketName( slip, slip.ht , slip.at)  }}</span>
                                    ({{ selectionTranslation(slip) }})
                                </p>
                                <p class="text-neutral-300 text-xs">{{ toFixedTwo(slip.p) }}</p>
                            </div>
                        </div>
                    </div>
                </li>
            </ul>
        </div>
        <template v-if="!collapsed">
            <div class="border-t border-neutral-500/30 grid grid-cols-2 px-4 py-3">   
                <MyBetFooterAmount
                    label="Stake"
                    :value="ticket.am"
                    :currency="getCurrency(ticket.cuid).cod"
                />
                <MyBetFooterAmount
                    label="Total Return"
                    :value="ticket.jackpot_win"
                    :currency="getCurrency(ticket.cuid).cod"
                />
            </div>
        </template>
    </div>
</template>
<style scoped>
.myb-Ticket {
    background-image: var(--mgc-bg);
    @apply rounded-md overflow-hidden;
}

.myb-TicketHeader {
    @apply h-14 px-4 flex items-center justify-between cursor-pointer;
}

.myb-TicketStatus {
    @apply text-xs;
}
.myb-TicketStatus.win {
    @apply text-emerald-400
}
.myb-TicketStatus.lost {
    color: red;
}

.myb-TicketHeaderNr {
    @apply font-bold;
    color: var(--color-primary4)
}
.myb-TicketHeaderAct{
    @apply flex items-center gap-2
}
.myb-TicketBetslips {
    @apply border-t border-neutral-500/30 px-4 py-2;
}

.myb-TicketBetslip {
    @apply flex;
}
.myb-SlipTeams{
    width: 100%;
    padding-right: 4px;
    display: -webkit-box;
    -webkit-line-clamp: 1; 
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal; 
    word-break: break-word; 
}
.myb-SlipTeams::after {
    content: '>';
    font: 28px "Consolas", monospace;
    color: #bababa;
    -webkit-transform: rotate(90deg);
    -moz-transform: rotate(90deg);
    -ms-transform: rotate(90deg);
    transform: rotate(90deg);
    right: 0;
    padding: 0 0 0;
    position: absolute;
    pointer-events: none;
    top: -1px;
}
</style>
